import React, { useMemo, useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Checkbox,
  FormControlLabel,
} from '@mui/material'

export interface PagedDisplayProps<T> {
  items: T[]
  title: string
  renderItem: (item: T, index: number) => React.ReactNode
  checkable?: boolean
  allSelected?: boolean
  someSelected?: boolean
  onSelectAll?: (selected: boolean) => void
  selectedCount?: number
  pageSizeOptions?: number[]
  defaultPageSize?: number
  maxHeight?: number
  filterOptions?: {
    label: string
    value: string
  }[]
  getFilterValue?: (item: T) => string
  filterLabel?: string
}

export function PagedDisplay<T>({
  items,
  title,
  renderItem,
  checkable = false,
  allSelected,
  someSelected,
  onSelectAll,
  selectedCount,
  pageSizeOptions = [10, 20, 50, 100],
  defaultPageSize = 20,
  maxHeight = 400,
  filterOptions,
  getFilterValue,
  filterLabel = 'Filter',
}: PagedDisplayProps<T>) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [selectedFilter, setSelectedFilter] = useState<string>('all')
  const [containerHeight, setContainerHeight] = useState(maxHeight)
  const [isResizing, setIsResizing] = useState(false)
  const resizeRef = useRef<HTMLDivElement>(null)
  const startYRef = useRef<number>(0)
  const startHeightRef = useRef<number>(maxHeight)

  const filteredItems = useMemo(() => {
    if (!filterOptions || !getFilterValue || selectedFilter === 'all') {
      return items
    }
    return items.filter((item) => getFilterValue(item) === selectedFilter)
  }, [items, filterOptions, getFilterValue, selectedFilter])

  const paginatedItems = useMemo(() => {
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredItems.slice(startIndex, endIndex)
  }, [filteredItems, page, pageSize])

  const totalPages = Math.ceil(filteredItems.length / pageSize)

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value)
  }

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newPageSize = Number(event.target.value)
    setPageSize(newPageSize)
    // Reset to page 1 when page size changes
    setPage(1)
  }

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    setSelectedFilter(event.target.value)
    // Reset to page 1 when filter changes
    setPage(1)
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return

      const deltaY = e.clientY - startYRef.current
      const newHeight = startHeightRef.current + deltaY
      const minHeight = 100
      const maxHeightLimit = 800
      const clampedHeight = Math.max(
        minHeight,
        Math.min(maxHeightLimit, newHeight),
      )
      setContainerHeight(clampedHeight)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'row-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
    startYRef.current = e.clientY
    startHeightRef.current = containerHeight
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
        flexWrap="wrap"
        gap={1}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 'bold', fontStyle: 'italic' }}
        >
          {title}
          {selectedCount !== undefined &&
            ` (${selectedCount} entries selected)`}
          {selectedCount === undefined && ` (${filteredItems.length} entries)`}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {filterOptions && getFilterValue && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{filterLabel}</InputLabel>
              <Select
                value={selectedFilter}
                label={filterLabel}
                onChange={handleFilterChange}
              >
                <MenuItem value="all">All</MenuItem>
                {filterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Page Size</InputLabel>
            <Select
              value={pageSize}
              label="Page Size"
              onChange={handlePageSizeChange}
            >
              {pageSizeOptions.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {checkable && onSelectAll && (
        <Box display="flex" alignItems="center" mb={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected || false}
                indeterminate={someSelected}
                onChange={(e) => onSelectAll(e.target.checked)}
              />
            }
            label={`Select All (applies to all ${items.length} entries)`}
          />
        </Box>
      )}

      <Box
        sx={{
          position: 'relative',
          height: containerHeight,
          overflowY: 'auto',
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1,
          mb: 2,
        }}
      >
        {paginatedItems.map((item, index) => renderItem(item, index))}
        <Box
          ref={resizeRef}
          onMouseDown={handleResizeStart}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 8,
            cursor: 'row-resize',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 40,
              height: 4,
              backgroundColor: 'grey.400',
              borderRadius: 2,
            },
          }}
        />
      </Box>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {filteredItems.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
          mt={1}
        >
          Showing {paginatedItems.length > 0 ? (page - 1) * pageSize + 1 : 0}-
          {Math.min(page * pageSize, filteredItems.length)} of{' '}
          {filteredItems.length} entries
        </Typography>
      )}
    </Box>
  )
}
